// Settings → Households — manage which households the user belongs
// to, who's in them, and which one is currently active for the
// meal-plan / pantry / shopping surfaces (v49).
//
// Most users will only ever see their own personal household plus the
// one(s) they've joined. The list-and-switch pattern below mirrors the
// chat thread picker — minimal chrome, single tap to switch, expand
// to manage members.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type Household, type HouseholdMember } from "../../lib/rpc";
import { confirm } from "../../components/confirm";
import type { Me } from "../../lib/use-me";
import { AddKidModal } from "./add-kid-modal";
import { KidDeviceModal } from "./kid-device-modal";
import { KidPinModal } from "./kid-pin-modal";

interface SettingsHouseholdsProps {
  me: Me | null;
  notify: (msg: string) => void;
}

export function SettingsHouseholds({ me, notify }: SettingsHouseholdsProps) {
  const [households, setHouseholds] = useState<Household[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [createDraft, setCreateDraft] = useState("");
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.householdsList();
      setHouseholds(r.households);
      setActiveId(r.activeHouseholdId);
    } catch (err) {
      notify((err as Error)?.message || "couldn't load your family");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const onSwitch = async (h: Household) => {
    if (h.id === activeId) return;
    try {
      await api.householdsSetActive(h.id);
      setActiveId(h.id);
      notify(`Now viewing ${h.name}`);
      // Meal-plan / pantry / shopping caches are keyed on user, not
      // household, so a hard reload is the cleanest way to swap
      // contexts without leaving stale data on-screen.
      setTimeout(() => window.location.reload(), 400);
    } catch (err) {
      notify((err as Error)?.message || "switch failed");
    }
  };

  const onCreate = async () => {
    const name = createDraft.trim();
    if (!name) return;
    setCreating(true);
    try {
      await api.householdsCreate(name);
      setCreateDraft("");
      await refresh();
      notify(`Created "${name}"`);
    } catch (err) {
      notify((err as Error)?.message || "create failed");
    } finally {
      setCreating(false);
    }
  };

  const onRename = async (h: Household) => {
    const next = window.prompt("Rename family", h.name);
    if (!next || next.trim() === h.name) return;
    try {
      await api.householdsRename(h.id, next.trim());
      await refresh();
      notify("Renamed");
    } catch (err) {
      notify((err as Error)?.message || "rename failed");
    }
  };

  const onLeave = async (h: Household) => {
    const ok = await confirm({
      title: `Leave "${h.name}"?`,
      message: "You'll lose access to this family's meal plan, pantry, and shopping lists. Other members keep theirs.",
      confirmLabel: "Leave",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.householdsLeave(h.id);
      await refresh();
      notify("Left household");
    } catch (err) {
      notify((err as Error)?.message || "couldn't leave");
    }
  };

  const onDelete = async (h: Household) => {
    const ok = await confirm({
      title: `Delete "${h.name}"?`,
      message: "Deletes the family for everyone. Refuses if there's still data attached — clear meal plans, pantry, and shopping first.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.householdsDelete(h.id);
      await refresh();
      notify("Deleted");
    } catch (err) {
      notify((err as Error)?.message || "couldn't delete");
    }
  };

  return (
    <div className="settings-section">
      <h3 className="settings-section-title">Your family</h3>
      <p className="settings-section-sub">
        Your family shares meal plans, pantry, and shopping lists.
        Everyone belongs to one — the owner adds members by username.
        Recipes are a global repo, not family-scoped.
      </p>

      {loading && (
        <p style={{ color: "var(--text-dim)", fontFamily: "var(--f-mono)", fontSize: 12 }}>loading…</p>
      )}

      {!loading && (
        <div className="settings-households">
          {households.map((h) => (
            <HouseholdCard
              key={h.id}
              h={h}
              me={me}
              isActive={h.id === activeId}
              expanded={expandedId === h.id}
              onExpand={() => setExpandedId(expandedId === h.id ? null : h.id)}
              onSwitch={() => onSwitch(h)}
              onRename={() => onRename(h)}
              onLeave={() => onLeave(h)}
              onDelete={() => onDelete(h)}
              onMembersChanged={refresh}
              notify={notify}
            />
          ))}

          {/* One household per user: the create box only appears when you
           *  don't belong to one yet. Once you're in a household you join
           *  others by being added (owner adds you by username), not by
           *  spinning up a second household. The server enforces this too
           *  (households.create throws "you're already in a household"). */}
          {households.length === 0 && (
            <div className="settings-household-add">
              <input
                placeholder="Name your family (e.g. The Beemers)"
                value={createDraft}
                onInput={(e) => setCreateDraft((e.currentTarget as HTMLInputElement).value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void onCreate(); } }}
                maxLength={80}
                disabled={creating}
              />
              <button className="manage-tags-add-btn" type="button" onClick={onCreate} disabled={!createDraft.trim() || creating}>
                Create
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface HouseholdCardProps {
  h: Household;
  me: Me | null;
  isActive: boolean;
  expanded: boolean;
  onExpand: () => void;
  onSwitch: () => void;
  onRename: () => void;
  onLeave: () => void;
  onDelete: () => void;
  onMembersChanged: () => void;
  notify: (msg: string) => void;
}

function HouseholdCard({ h, me, isActive, expanded, onExpand, onSwitch, onRename, onLeave, onDelete, onMembersChanged, notify }: HouseholdCardProps) {
  const isOwner = h.role === "owner";
  // Admins get the same day-to-day management surface as owners — add /
  // remove members + kids, rename, manage the shared kids login.
  // Owner-exclusive ops (delete, promote/demote admins) remain gated
  // off `isOwner`.
  const canManage = h.role === "owner" || h.role === "admin";
  return (
    <div className={`settings-household-card${isActive ? " is-active" : ""}`}>
      <header className="settings-household-head">
        <button className="settings-household-name" onClick={onExpand} type="button">
          <span className="settings-household-title">{h.name}</span>
          <span className="settings-household-meta">
            {h.role}
            {" · "}
            {h.memberCount} {h.memberCount === 1 ? "member" : "members"}
            {isActive && (
              <>
                {" · "}
                <span className="settings-household-active-tag">active</span>
              </>
            )}
          </span>
        </button>
        {!isActive && (
          <button className="settings-household-switch" type="button" onClick={onSwitch} title="Switch to this family">
            Switch
          </button>
        )}
      </header>

      {expanded && (
        <div className="settings-household-body">
          <MembersList h={h} me={me} isOwner={isOwner} canManage={canManage} onChanged={onMembersChanged} notify={notify} />
          <div className="settings-household-actions">
            {canManage && (
              <button className="settings-household-action" type="button" onClick={onRename}>
                <Icon name="edit" size={11} /> Rename
              </button>
            )}
            <button className="settings-household-action" type="button" onClick={onLeave}>
              <Icon name="logout" size={11} /> Leave
            </button>
            {isOwner && (
              <button className="settings-household-action settings-household-action-danger" type="button" onClick={onDelete}>
                <Icon name="trash" size={11} /> Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface MembersListProps {
  h: Household;
  me: Me | null;
  /** Caller is the owner — gets promote/demote/delete-only powers. */
  isOwner: boolean;
  /** Caller is owner OR admin — gets add/remove members + kids, PIN reset,
   *  shared-login management. Day-to-day stuff. */
  canManage: boolean;
  onChanged: () => void;
  notify: (msg: string) => void;
}

function MembersList({ h, me, isOwner, canManage, onChanged, notify }: MembersListProps) {
  const [members, setMembers] = useState<HouseholdMember[]>([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addKidOpen, setAddKidOpen] = useState(false);
  // Shared kid-device login status for this household. `null` when the
  // feature isn't configured server-side (AUTH0_M2M_* env unset) so we
  // don't render the affordance at all.
  const [deviceStatus, setDeviceStatus] = useState<{ provisioned: boolean; email: string } | null>(null);
  const [deviceModal, setDeviceModal] = useState<{ mode: "provision" | "reset"; email: string } | null>(null);
  const [pinModalKid, setPinModalKid] = useState<{ id: string; name: string } | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.householdsListMembers(h.id);
      setMembers(r);
    } catch (err) {
      notify((err as Error)?.message || "couldn't load members");
    } finally {
      setLoading(false);
    }
  };

  // Shared-login status lookup — owner or admin. The server returns
  // auth0_mgmt_not_configured if the env vars aren't set; we swallow that
  // and leave the affordance hidden rather than confuse the parent.
  const refreshDeviceStatus = async () => {
    if (!canManage) { setDeviceStatus(null); return; }
    try {
      const r = await api.householdsKidDeviceStatus(h.id);
      setDeviceStatus({ provisioned: r.provisioned, email: r.email });
    } catch (err) {
      setDeviceStatus(null);
    }
  };

  useEffect(() => { void refresh(); }, [h.id]);
  useEffect(() => { if (!loading) void refreshDeviceStatus(); }, [loading, h.id]);

  const onAdd = async () => {
    const userId = draft.trim();
    if (!userId) return;
    setAdding(true);
    try {
      await api.householdsAddMember(h.id, userId);
      setDraft("");
      await refresh();
      onChanged();
      notify(`Added ${userId}`);
    } catch (err) {
      notify((err as Error)?.message || "couldn't add member");
    } finally {
      setAdding(false);
    }
  };

  const onRemove = async (m: HouseholdMember) => {
    const ok = await confirm({
      title: `Remove ${m.userId}?`,
      message: `They lose access to this family's meal plan, pantry, and shopping lists.`,
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.householdsRemoveMember(h.id, m.userId);
      await refresh();
      onChanged();
      notify("Removed");
    } catch (err) {
      notify((err as Error)?.message || "couldn't remove");
    }
  };

  // Owner-only: promote a member to admin (co-parent management role).
  const onPromote = async (m: HouseholdMember) => {
    try {
      await api.householdsPromoteAdmin(h.id, m.userId);
      await refresh();
      onChanged();
      notify(`${m.userId} is now an admin`);
    } catch (err) {
      notify((err as Error)?.message || "couldn't promote");
    }
  };

  // Owner-only: demote an admin back to plain member.
  const onDemote = async (m: HouseholdMember) => {
    const ok = await confirm({
      title: `Demote ${m.userId}?`,
      message: "They keep their household membership but lose admin powers.",
      confirmLabel: "Demote",
    });
    if (!ok) return;
    try {
      await api.householdsDemoteAdmin(h.id, m.userId);
      await refresh();
      onChanged();
      notify(`${m.userId} is now a member`);
    } catch (err) {
      notify((err as Error)?.message || "couldn't demote");
    }
  };

  return (
    <div className="settings-household-members">
      <div className="settings-household-members-label">Members</div>
      {loading && <div className="settings-household-members-empty">loading…</div>}
      {!loading && members.map((m) => {
        // Admin can remove kids + plain members. Only the owner can
        // remove or demote another admin. Nobody removes the owner from
        // this row (the owner uses Leave + transfer).
        const targetIsOwner = m.role === "owner";
        const targetIsAdmin = m.role === "admin";
        const isSelf = m.userId === me?.username;
        const canRemove =
          !isSelf && !targetIsOwner && (isOwner || (canManage && !targetIsAdmin));
        return (
          <div key={m.userId} className="settings-household-member">
            <span className="settings-household-member-name">
              {m.userId}
              {isSelf && <span className="settings-household-member-you">  you</span>}
            </span>
            <span className={`settings-household-member-role${targetIsAdmin ? " is-admin" : ""}`}>{m.role}</span>
            {/* Owner-only: promote a plain member to admin or demote an
             *  admin back to member. Self is excluded — the owner can't
             *  demote/promote themselves; transferring ownership is a
             *  separate (still-to-build) flow. */}
            {isOwner && !isSelf && m.role === "member" && (
              <button
                className="manage-tags-done"
                type="button"
                title="Make this member a household admin (co-parent)"
                style={{ padding: "1px 8px", fontSize: 11, marginRight: 4 }}
                onClick={() => onPromote(m)}
              >
                <Icon name="people" size={11} /> Make admin
              </button>
            )}
            {isOwner && !isSelf && targetIsAdmin && (
              <button
                className="manage-tags-done"
                type="button"
                title="Demote admin back to member"
                style={{ padding: "1px 8px", fontSize: 11, marginRight: 4 }}
                onClick={() => onDemote(m)}
              >
                <Icon name="people" size={11} /> Demote
              </button>
            )}
            {canManage && m.role === "kid" && (
              <button
                className="manage-tags-done"
                type="button"
                title="Set or reset this kid's PIN"
                style={{ padding: "1px 8px", fontSize: 11, marginRight: 4 }}
                onClick={() => setPinModalKid({ id: m.userId, name: m.userId })}
              >
                <Icon name="key" size={11} /> PIN
              </button>
            )}
            {canRemove && (
              <button className="icon-btn settings-household-member-remove" onClick={() => onRemove(m)} type="button" title="Remove">
                <Icon name="x" size={11} />
              </button>
            )}
          </div>
        );
      })}
      {canManage && (
        <>
          <div className="settings-household-add-member">
            <input
              placeholder="Add member by username"
              value={draft}
              onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void onAdd(); } }}
              maxLength={32}
              disabled={adding}
            />
            <button className="settings-household-add-btn" type="button" onClick={onAdd} disabled={!draft.trim() || adding}>
              Add
            </button>
          </div>
          <button className="settings-household-add-kid" type="button" onClick={() => setAddKidOpen(true)}>
            <Icon name="plus" size={11} /> Add a kid
          </button>
        </>
      )}

      {/* Shared kids login — one Auth0 account per household that the
          family types into their tablet. Available to owner + admin when
          Auth0 Management is configured server-side. */}
      {canManage && deviceStatus && (
        <div className="settings-household-shared-login">
          <div className="settings-household-members-label">Shared kids login</div>
          {deviceStatus.provisioned ? (
            <div className="settings-household-shared-login-row">
              <span className="settings-household-shared-login-email">{deviceStatus.email}</span>
              <button
                className="manage-tags-done"
                type="button"
                title="Reset shared login password"
                style={{ padding: "1px 8px", fontSize: 11 }}
                onClick={() => setDeviceModal({ mode: "reset", email: deviceStatus.email })}
              >
                <Icon name="key" size={11} /> Reset password
              </button>
            </div>
          ) : (
            <button
              className="settings-household-add-kid"
              type="button"
              onClick={() => setDeviceModal({ mode: "provision", email: deviceStatus.email })}
            >
              <Icon name="key" size={11} /> Set up shared kids login
            </button>
          )}
        </div>
      )}

      <AddKidModal
        open={addKidOpen}
        householdId={h.id}
        onClose={() => setAddKidOpen(false)}
        onCreated={() => { void refresh(); onChanged(); }}
        notify={notify}
      />

      <KidDeviceModal
        open={!!deviceModal}
        mode={deviceModal?.mode || "provision"}
        householdId={h.id}
        email={deviceModal?.email || ""}
        onClose={() => setDeviceModal(null)}
        onSuccess={() => { void refreshDeviceStatus(); }}
        notify={notify}
      />

      <KidPinModal
        open={!!pinModalKid}
        kidId={pinModalKid?.id || ""}
        kidName={pinModalKid?.name || ""}
        onClose={() => setPinModalKid(null)}
        onSuccess={() => {}}
        notify={notify}
      />
    </div>
  );
}
